import { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import { api, formatApiError } from "../../lib/api";
import { Plus, Download, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminFees() {
  const [fees, setFees] = useState(null);
  const [students, setStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [monthFilter, setMonthFilter] = useState(defaultMonth());
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentModeFilter, setPaymentModeFilter] = useState("all");
  const [feeTypeFilter, setFeeTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    student_id: "", month: defaultMonth(), amount: "", type: "rent", status: "paid", method: "cash", note: ""
  });
  const [saving, setSaving] = useState(false);

  const loadFiltersData = useCallback(async () => {
    const [s, r] = await Promise.all([api.get("/admin/students"), api.get("/admin/rooms")]);
    setStudents(s.data); setRooms(r.data);
  }, []);

  const loadFees = useCallback(async () => {
    const params = {};
    if (monthFilter) params.month = monthFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (paymentModeFilter !== "all") params.payment_mode = paymentModeFilter;
    if (feeTypeFilter !== "all") params.fee_type = feeTypeFilter;
    const f = await api.get("/admin/fees", { params });
    setFees(f.data);
  }, [monthFilter, statusFilter, paymentModeFilter, feeTypeFilter]);

  useEffect(() => { loadFiltersData(); }, [loadFiltersData]);
  useEffect(() => { loadFees(); }, [loadFees]);

  // Auto-suggest amount from room category when student or type changes
  useEffect(() => {
    if (!form.student_id || !rooms.length) return;
    const stu = students.find((s) => s.id === form.student_id);
    if (!stu?.room_id) return;
    const room = rooms.find((r) => r.id === stu.room_id);
    if (!room?.category) return;
    if (form.type === "rent" && !form.amount) setForm((f) => ({ ...f, amount: String(room.category.monthly_rent) }));
    if (form.type === "deposit" && !form.amount) setForm((f) => ({ ...f, amount: String(room.category.security_deposit) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.student_id, form.type]);

  const totals = useMemo(() => {
    const list = fees || [];
    const paid = list.filter((x) => x.status === "paid").reduce((a, b) => a + Number(b.amount), 0);
    const unpaid = list.filter((x) => x.status !== "paid").reduce((a, b) => a + Number(b.amount), 0);
    return { paid, unpaid };
  }, [fees]);

  const rows = fees || [];

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/fees", { ...form, amount: Number(form.amount) });
      toast.success("Fee record saved");
      setOpen(false);
      setForm({ student_id: "", month: defaultMonth(), amount: "", type: "rent", status: "paid", method: "cash", note: "" });
      loadFees();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function remove(f) {
    if (!window.confirm("Delete this fee record?")) return;
    await api.delete(`/admin/fees/${f.id}`);
    toast.success("Deleted"); loadFees();
  }

  async function exportCsv() {
    try {
      const params = {};
      if (monthFilter) params.month = monthFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      if (paymentModeFilter !== "all") params.payment_mode = paymentModeFilter;
      if (feeTypeFilter !== "all") params.fee_type = feeTypeFilter;

      const res = await api.get("/admin/fees/export", {
        params,
        responseType: "blob",
      });

      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `fees-${monthFilter || "all"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("CSV exported");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  function clearFilters() {
    setMonthFilter(defaultMonth());
    setStatusFilter("all");
    setPaymentModeFilter("all");
    setFeeTypeFilter("all");
  }

  function downloadReceipt(f) {
    window.open(`${BACKEND_URL}/api/fees/${f.id}/receipt`, "_blank");
  }

  function whatsappReminder(f, target = "student") {
    const stu = students.find((s) => s.id === f.student_id);
    if (!stu) { toast.error("Resident not found"); return; }
    const rawPhone = target === "parent" ? stu.parents_mobile : stu.mobile;
    if (!rawPhone) { toast.error("No phone number on file"); return; }
    // Strip non-digits; assume India (+91) if 10 digits and no country code
    let digits = String(rawPhone).replace(/\D/g, "");
    if (digits.length === 10) digits = "91" + digits;
    const greet = target === "parent" ? `Namaste, this is Cozy Heaven PG reaching out about ${stu.name}'s` : `Hi ${stu.name?.split(" ")[0] || ""}, gentle reminder from Cozy Heaven PG —`;
    const msg = `${greet} pending fee of ₹${Number(f.amount).toLocaleString()} for ${f.month}. Please pay at your earliest convenience. Thank you! — Cozy Heaven`;
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  }

  return (
    <AppShell>
      <PageHeader
        overline="Money matters"
        title="Fee records"
        subtitle="Track who paid, who hasn't, and download crisp PDF receipts."
        actions={
          <button onClick={() => setOpen(true)} data-testid="add-fee-btn" className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Record fee
          </button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <SummaryCard label="Total collected" value={`₹ ${totals.paid.toLocaleString()}`} tone="ok" />
        <SummaryCard label="Outstanding" value={`₹ ${totals.unpaid.toLocaleString()}`} tone="warn" />
        <SummaryCard label="Records" value={(fees || []).length} tone="muted" />
      </div>

      <Panel
        title="Ledger"
        action={
          <div className="flex items-center gap-2">
            <button onClick={clearFilters} className="bg-white border border-warm text-deep px-3 py-2 rounded-md text-sm" title="Reset all filters">
              Clear filters
            </button>
            <button onClick={exportCsv} className="bg-white border border-warm text-deep px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" title="Export current filtered list as CSV">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <FieldL label="Month">
            <input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className={input} />
          </FieldL>
          <FieldL label="Payment status">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={input}>
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
            </select>
          </FieldL>
          <FieldL label="Payment mode">
            <select value={paymentModeFilter} onChange={(e) => setPaymentModeFilter(e.target.value)} className={input}>
              <option value="all">All</option>
              <option value="cash">Cash</option>
              <option value="online">Online</option>
            </select>
          </FieldL>
          <FieldL label="Fee type">
            <select value={feeTypeFilter} onChange={(e) => setFeeTypeFilter(e.target.value)} className={input}>
              <option value="all">All</option>
              <option value="rent">Monthly rent</option>
              <option value="deposit">Security deposit</option>
            </select>
          </FieldL>
        </div>

        {fees === null ? (
          <div className="text-sm text-warm">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No fee records" hint="Record the first payment to see it here." />
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-[#F4F1EA] text-warm uppercase tracking-wider text-xs">
                  <th className="text-left px-6 py-3">Receipt</th>
                  <th className="text-left px-6 py-3">Resident</th>
                  <th className="text-left px-6 py-3">Month</th>
                  <th className="text-left px-6 py-3">Amount</th>
                  <th className="text-left px-6 py-3">Method</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id} className="border-t border-warm hover:bg-[#FDFBF7]">
                    <td className="px-6 py-3 font-mono text-xs">{f.receipt_no}</td>
                    <td className="px-6 py-3"><div className="font-medium text-deep">{f.student_name}</div><div className="text-xs text-warm">Room {f.room_number || "—"}</div></td>
                    <td className="px-6 py-3">
                      <div>{f.month}</div>
                      {f.type && f.type !== "rent" && <div className="text-xs text-warm capitalize">{f.type === "deposit" ? "security deposit" : f.type}</div>}
                    </td>
                    <td className="px-6 py-3 font-medium">₹ {Number(f.amount).toLocaleString()}</td>
                    <td className="px-6 py-3 capitalize">{f.method || "—"}</td>
                    <td className="px-6 py-3"><StatusPill status={f.status === "unpaid" ? "pending" : f.status} /></td>
                    <td className="px-6 py-3 text-right">
                      {f.status === "paid" && (
                        <button onClick={() => downloadReceipt(f)} data-testid={`download-receipt-${f.id}`} className="p-2 hover:bg-[#F4F1EA] rounded-md" title="Download receipt">
                          <Download className="h-4 w-4 text-warm" />
                        </button>
                      )}
                      {f.status !== "paid" && (
                        <>
                          <button onClick={() => whatsappReminder(f, "student")} data-testid={`whatsapp-student-${f.id}`} className="p-2 hover:bg-[#DCE3D6]/60 rounded-md" title="WhatsApp resident">
                            <MessageCircle className="h-4 w-4 text-[#4A5D3E]" />
                          </button>
                          <button onClick={() => whatsappReminder(f, "parent")} data-testid={`whatsapp-parent-${f.id}`} className="p-2 hover:bg-[#DCE3D6]/60 rounded-md" title="WhatsApp parent">
                            <MessageCircle className="h-4 w-4 text-[#8C6D31]" />
                          </button>
                        </>
                      )}
                      <button onClick={() => remove(f)} className="p-2 hover:bg-[#F5D8D8] rounded-md"><Trash2 className="h-4 w-4 text-[#8C3F3F]" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {open && (
        <Modal title="Record a fee payment" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2">
              <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Resident *</span>
              <select required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className={input} data-testid="fee-student-select">
                <option value="">Select…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name} — Room {s.room_number || "—"}</option>)}
              </select>
            </label>
            <FieldL label="Billing month *"><input required type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={input} /></FieldL>
            <FieldL label="Type *">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, amount: "" })} className={input} data-testid="fee-type">
                <option value="rent">Rent</option><option value="deposit">Security deposit</option><option value="other">Other</option>
              </select>
            </FieldL>
            <FieldL label="Amount (₹) *"><input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={input} data-testid="fee-amount" /></FieldL>
            <FieldL label="Status *">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={input}>
                <option value="paid">Paid</option>
                <option value="unpaid">Pending</option>
              </select>
            </FieldL>
            <FieldL label="Method">
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className={input}>
                <option value="cash">Cash</option><option value="upi">UPI</option><option value="bank">Bank</option><option value="card">Card</option>
              </select>
            </FieldL>
            <label className="block sm:col-span-2">
              <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Note</span>
              <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} />
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-fee-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";

function FieldL({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">{label}</span>{children}</label>;
}

function SummaryCard({ label, value, tone }) {
  const toneCls = tone === "ok" ? "bg-[#DCE3D6]" : tone === "warn" ? "bg-[#FBEED7]" : "bg-[#F4F1EA]";
  return (
    <div className="bg-white border border-warm rounded-lg p-5">
      <div className={`inline-block h-1.5 w-12 rounded-full mb-3 ${toneCls}`} />
      <div className="font-body font-semibold text-3xl text-deep tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-warm mt-1">{label}</div>
    </div>
  );
}

function defaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
